import { Button } from "@design-system/components/Button";
import { trans } from "@mongez/localization";
import { FaCopy } from "react-icons/fa";
import {
  useCurrentPaymentMethod,
  usePaymentMethodCopyField,
  usePaymentSettings,
} from "../hooks";

export function PaymentDetails() {
  const { bankTransferSettings, mobileWalletsSettings } = usePaymentSettings();
  const { paymentMethod } = useCurrentPaymentMethod();
  const { copiedField, onCopy } = usePaymentMethodCopyField();

  const renderVodafoneCashDetails = () => {
    const vodafoneData = mobileWalletsSettings?.vodafoneCash;
    if (!vodafoneData) return null;

    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20 p-4">
        <h3 className="mb-3 font-semibold text-gray-900 dark:text-gray-100">
          {trans("invoices.paymentDetails")}
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg bg-white dark:bg-gray-800 p-3">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {trans("invoices.vodafoneCashNumber")}
              </p>
              <p className="font-mono text-lg text-gray-700 dark:text-gray-300">{vodafoneData}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onCopy(vodafoneData, "vodafone")}
              className="ml-2"
            >
              <FaCopy size={14} />
              <span className="ml-1">
                {copiedField === "vodafone"
                  ? trans("invoices.copied")
                  : trans("invoices.copyToClipboard")}
              </span>
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderInstaPayDetails = () => {
    const instaPayData = mobileWalletsSettings?.instaPay;
    if (!instaPayData) return null;

    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20 p-4">
        <h3 className="mb-3 font-semibold text-gray-900 dark:text-gray-100">
          {trans("invoices.paymentDetails")}
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg bg-white dark:bg-gray-800 p-3">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {trans("invoices.instaPayNumber")}
              </p>
              <p className="font-mono text-lg text-gray-700 dark:text-gray-300">{instaPayData}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onCopy(instaPayData, "instaPay")}
              className="ml-2"
            >
              <FaCopy size={14} />
              <span className="ml-1">
                {copiedField === "instaPay"
                  ? trans("invoices.copied")
                  : trans("invoices.copyToClipboard")}
              </span>
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderBankTransferDetails = () => {
    if (!bankTransferSettings) return null;

    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
        <h3 className="mb-3 font-semibold text-gray-900 dark:text-gray-100">
          {trans("invoices.bankAccountDetails")}
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg bg-white dark:bg-gray-800 p-3">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {trans("invoices.bankName")}
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300">
                {bankTransferSettings.bankName}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-white dark:bg-gray-800 p-3">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {trans("invoices.accountNumber")}
              </p>
              <p className="font-mono text-lg text-gray-700 dark:text-gray-300">
                {bankTransferSettings.accountNumber}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                onCopy(bankTransferSettings.accountNumber, "accountNumber")
              }
              className="ml-2"
            >
              <FaCopy size={14} />
              <span className="ml-1">
                {copiedField === "accountNumber"
                  ? trans("invoices.copied")
                  : trans("invoices.copyToClipboard")}
              </span>
            </Button>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-white dark:bg-gray-800 p-3">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {trans("invoices.iban")}
              </p>
              <p className="font-mono text-lg text-gray-700 dark:text-gray-300">
                {bankTransferSettings.accountIban}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onCopy(bankTransferSettings.accountIban, "iban")}
              className="ml-2"
            >
              <FaCopy size={14} />
              <span className="ml-1">
                {copiedField === "iban"
                  ? trans("invoices.copied")
                  : trans("invoices.copyToClipboard")}
              </span>
            </Button>
          </div>
        </div>
      </div>
    );
  };

  switch (paymentMethod) {
    case "vodafoneCash":
      return renderVodafoneCashDetails();
    case "instaPay":
      return renderInstaPayDetails();
    case "bankTransfer":
      return renderBankTransferDetails();
    default:
      return null;
  }
}
